import { useState } from 'react';
import { User, ChevronLeft, ChevronRight, Waves, Circle, DoorOpen } from 'lucide-react';

interface SeatMapProps {
  selectedSeats: string[];
  onSeatSelect: (seatNumber: string) => void;
}

type SeatStatus = 'available' | 'occupied' | 'female' | 'selected';

interface Seat {
  number: string;
  status: SeatStatus;
  type?: 'sleeper' | 'regular';
}

export const SeatMap = ({ selectedSeats, onSeatSelect }: SeatMapProps) => {
  // 🛡️ Defensive Guards
  const safeSelectedSeats = selectedSeats ?? [];
  
  const [currentFloor, setCurrentFloor] = useState<'lower' | 'upper'>('upper');

  // Lower deck layout (12 seats + 2 bathrooms + steering wheel + 2 stairs)
  // Estructura: Volante | Pasillo | Entrada
  //             Escalera 2do piso | Pasillo | Espacio
  //             2 asientos izq | Pasillo | 2 asientos der (4 asientos)
  //             Baño H | Pasillo | Baño M
  //             4 filas de 2 asientos cada lado (8 asientos)
  const lowerDeck: (Seat | null | string)[][] = [
    // Fila 1: Volante y entrada
    ['steering', null, null, 'entrance'],
    // Fila 2: Escalera segundo piso
    ['stairs-up', null, null, null],
    // Fila 3: Primera fila de asientos (4 asientos)
    [{ number: '1', status: 'occupied', type: 'regular' }, { number: '2', status: 'available', type: 'regular' }, null, { number: '3', status: 'available', type: 'regular' }, { number: '4', status: 'available', type: 'regular' }],
    // Fila 4: Baños
    ['bath-male', null, null, 'bath-female'],
    // Filas 5-8: 8 asientos (4 filas de 2 asientos por lado)
    [{ number: '5', status: 'available', type: 'regular' }, { number: '6', status: 'available', type: 'regular' }, null, { number: '7', status: 'available', type: 'regular' }, { number: '8', status: 'available', type: 'regular' }],
    [{ number: '9', status: 'available', type: 'regular' }, { number: '10', status: 'available', type: 'regular' }, null, { number: '11', status: 'available', type: 'regular' }, { number: '12', status: 'available', type: 'regular' }],
    [{ number: '13', status: 'available', type: 'regular' }, { number: '14', status: 'available', type: 'regular' }, null, { number: '15', status: 'available', type: 'regular' }, { number: '16', status: 'available', type: 'regular' }],
    [{ number: '17', status: 'available', type: 'regular' }, { number: '18', status: 'available', type: 'regular' }, null, { number: '19', status: 'available', type: 'regular' }, { number: '20', status: 'available', type: 'regular' }],
  ];

  // Upper deck layout (42 seats)
  // Lado izq: 20 asientos (10 filas de 2) con escaleras después de 5 filas
  // Lado der: 22 asientos (11 filas de 2)
  const upperDeck: (Seat | null | string)[][] = [
    // Filas 1-5: Lado izq (10 asientos) | Pasillo | Lado der (10 asientos)
    [{ number: '21', status: 'available', type: 'regular' }, { number: '22', status: 'available', type: 'regular' }, null, { number: '23', status: 'available', type: 'regular' }, { number: '24', status: 'available', type: 'regular' }],
    [{ number: '25', status: 'available', type: 'regular' }, { number: '26', status: 'available', type: 'regular' }, null, { number: '27', status: 'available', type: 'regular' }, { number: '28', status: 'available', type: 'regular' }],
    [{ number: '29', status: 'available', type: 'regular' }, { number: '30', status: 'available', type: 'regular' }, null, { number: '31', status: 'available', type: 'regular' }, { number: '32', status: 'available', type: 'regular' }],
    [{ number: '33', status: 'occupied', type: 'regular' }, { number: '34', status: 'female', type: 'regular' }, null, { number: '35', status: 'available', type: 'regular' }, { number: '36', status: 'available', type: 'regular' }],
    [{ number: '37', status: 'available', type: 'regular' }, { number: '38', status: 'available', type: 'regular' }, null, { number: '39', status: 'available', type: 'regular' }, { number: '40', status: 'available', type: 'regular' }],
    // Fila 6: Escaleras lado izq | Pasillo | Asientos lado der
    ['stairs-down', null, null, { number: '41', status: 'available', type: 'regular' }, { number: '42', status: 'available', type: 'regular' }],
    // Filas 7-11: Lado izq (10 asientos) | Pasillo | Lado der (12 asientos)
    [{ number: '43', status: 'available', type: 'regular' }, { number: '44', status: 'available', type: 'regular' }, null, { number: '45', status: 'available', type: 'regular' }, { number: '46', status: 'available', type: 'regular' }],
    [{ number: '47', status: 'available', type: 'regular' }, { number: '48', status: 'available', type: 'regular' }, null, { number: '49', status: 'available', type: 'regular' }, { number: '50', status: 'available', type: 'regular' }],
    [{ number: '51', status: 'available', type: 'regular' }, { number: '52', status: 'available', type: 'regular' }, null, { number: '53', status: 'available', type: 'regular' }, { number: '54', status: 'available', type: 'regular' }],
    [{ number: '55', status: 'available', type: 'regular' }, { number: '56', status: 'available', type: 'regular' }, null, { number: '57', status: 'available', type: 'regular' }, { number: '58', status: 'available', type: 'regular' }],
    [{ number: '59', status: 'available', type: 'regular' }, { number: '60', status: 'available', type: 'regular' }, null, { number: '61', status: 'available', type: 'regular' }, { number: '62', status: 'available', type: 'regular' }],
  ];

  const getSeatClass = (seat: Seat) => {
    const isSelected = safeSelectedSeats.includes(seat.number);
    const baseClass = 'flex items-center justify-center rounded-[10px] transition-all cursor-pointer border-2 font-medium';
    const sizeClass = 'w-12 h-12';

    if (isSelected) {
      return `${baseClass} ${sizeClass} bg-[#FF6A2A] border-[#FF6A2A] text-white hover:bg-[#C2410C] hover:border-[#C2410C]`;
    }

    switch (seat.status) {
      case 'available':
        return `${baseClass} ${sizeClass} bg-white border-[#E5E7EB] text-[#111827] hover:border-[#FF6A2A] hover:bg-[#FFF5F0]`;
      case 'occupied':
        return `${baseClass} ${sizeClass} bg-[#E5E7EB] border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed`;
      case 'female':
        return `${baseClass} ${sizeClass} bg-[#FDF2F8] border-[#F9A8D4] text-[#EC4899] cursor-not-allowed`;
      default:
        return `${baseClass} ${sizeClass}`;
    }
  };

  const handleSeatClick = (seat: Seat) => {
    if (seat.status === 'available') {
      onSeatSelect(seat.number);
    }
  };

  const renderSeat = (seat: Seat, key: string) => {
    return (
      <button
        key={key}
        onClick={() => handleSeatClick(seat)}
        className={getSeatClass(seat)}
        disabled={seat.status === 'occupied' || seat.status === 'female'}
        title={`Asiento ${seat.number} - ${
          seat.status === 'available'
            ? 'Disponible'
            : seat.status === 'occupied'
            ? 'Ocupado'
            : seat.status === 'female'
            ? 'Reservado para mujeres'
            : ''
        }`}
      >
        {seat.status === 'female' ? (
          <User className="w-5 h-5" />
        ) : (
          <span className="text-sm font-semibold">{seat.number}</span>
        )}
      </button>
    );
  };

  const renderSpecialElement = (type: string, key: string) => {
    switch (type) {
      case 'steering':
        return (
          <div key={key} className="w-12 h-12 bg-[#111827] rounded-[10px] flex items-center justify-center border-2 border-[#111827]">
            <Circle className="w-6 h-6 text-white" />
          </div>
        );
      case 'entrance':
        return (
          <div key={key} className="w-12 h-12 bg-[#10B981] border-2 border-[#059669] rounded-[10px] flex items-center justify-center">
            <DoorOpen className="w-6 h-6 text-white" />
          </div>
        );
      case 'stairs-up':
      case 'stairs-down':
        return (
          <div key={key} className="w-12 h-12 bg-[#FEF3C7] border-2 border-[#FCD34D] rounded-[10px] flex items-center justify-center">
            <div className="text-2xl font-bold text-[#B45309] leading-none">⇅</div>
          </div>
        );
      case 'bath-male':
        return (
          <div key={key} className="w-12 h-12 bg-[#DBEAFE] border-2 border-[#93C5FD] rounded-[10px] flex items-center justify-center">
            <Waves className="w-6 h-6 text-[#1E40AF]" />
          </div>
        );
      case 'bath-female':
        return (
          <div key={key} className="w-12 h-12 bg-[#FCE7F3] border-2 border-[#F9A8D4] rounded-[10px] flex items-center justify-center">
            <Waves className="w-6 h-6 text-[#BE185D]" />
          </div>
        );
      default:
        return <div key={key} className="w-12"></div>;
    }
  };

  const renderLowerDeck = () => (
    <div className="flex flex-col items-center">
      <div className="mb-3 px-3 py-1.5 bg-[#111827] text-white text-xs font-semibold rounded-[8px]">
        Piso 1
      </div>
      
      <div className="border-2 border-[#E5E7EB] rounded-[16px] p-6 bg-gradient-to-b from-[#F7F8FA] to-white">
        <div className="space-y-3">
          {lowerDeck.map((row, rowIndex) => (
            <div key={rowIndex} className="flex items-center justify-center gap-3">
              {row.map((item, colIndex) => {
                if (item === null) {
                  return <div key={colIndex} className="w-12"></div>;
                }
                if (typeof item === 'string') {
                  return renderSpecialElement(item, `${rowIndex}-${colIndex}`);
                }
                return renderSeat(item as Seat, `${rowIndex}-${colIndex}`);
              })}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-[#6B7280]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#111827] rounded"></div>
          <span>Conductor</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#10B981] rounded"></div>
          <span>Entrada</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#FEF3C7] rounded"></div>
          <span>Escaleras</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#DBEAFE] rounded"></div>
          <span>Baño H</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#FCE7F3] rounded"></div>
          <span>Baño M</span>
        </div>
      </div>
      
      <div className="mt-2 text-xs text-[#111827] font-semibold">
        12 asientos
      </div>
    </div>
  );

  const renderUpperDeck = () => (
    <div className="flex flex-col items-center">
      <div className="mb-3 px-3 py-1.5 bg-[#111827] text-white text-xs font-semibold rounded-[8px]">
        Piso 2
      </div>
      
      <div className="border-2 border-[#E5E7EB] rounded-[16px] p-6 bg-gradient-to-b from-[#F7F8FA] to-white">
        <div className="space-y-3">
          {upperDeck.map((row, rowIndex) => (
            <div key={rowIndex} className="flex items-center gap-3">
              {row.map((item, colIndex) => {
                if (item === null) {
                  return <div key={colIndex} className="w-12"></div>;
                }
                if (typeof item === 'string') {
                  return renderSpecialElement(item, `${rowIndex}-${colIndex}`);
                }
                return renderSeat(item as Seat, `${rowIndex}-${colIndex}`);
              })}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-2 text-xs text-[#111827] font-semibold">
        42 asientos
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 p-4 bg-[#F7F8FA] rounded-[12px] border border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white border-2 border-[#E5E7EB] rounded-[8px]"></div>
          <span className="text-sm text-[#6B7280]">Disponible</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF6A2A] border-2 border-[#FF6A2A] rounded-[8px]"></div>
          <span className="text-sm text-[#6B7280]">Seleccionado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#E5E7EB] border-2 border-[#E5E7EB] rounded-[8px]"></div>
          <span className="text-sm text-[#6B7280]">Ocupado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FDF2F8] border-2 border-[#F9A8D4] rounded-[8px] flex items-center justify-center">
            <User className="w-4 h-4 text-[#EC4899]" />
          </div>
          <span className="text-sm text-[#6B7280]">Solo mujeres</span>
        </div>
      </div>

      {/* Desktop: Horizontal Layout (both floors side by side) */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-2 gap-8 items-start">
          {renderLowerDeck()}
          {renderUpperDeck()}
        </div>
      </div>

      {/* Mobile: Vertical Layout (one floor at a time with navigation) */}
      <div className="lg:hidden">
        {/* Floor Navigation */}
        <div className="flex items-center justify-between mb-4 bg-[#F7F8FA] rounded-[12px] p-3 border border-[#E5E7EB]">
          <button
            onClick={() => setCurrentFloor('lower')}
            disabled={currentFloor === 'lower'}
            className="p-2 rounded-[10px] bg-white border-2 border-[#E5E7EB] disabled:bg-[#FF6A2A] disabled:border-[#FF6A2A] disabled:text-white hover:border-[#FF6A2A] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <p className="text-sm font-semibold text-[#111827]">
              {currentFloor === 'lower' ? 'Piso 1' : 'Piso 2'}
            </p>
            <p className="text-xs text-[#6B7280]">
              {currentFloor === 'lower' ? '12 asientos' : '42 asientos'}
            </p>
          </div>
          
          <button
            onClick={() => setCurrentFloor('upper')}
            disabled={currentFloor === 'upper'}
            className="p-2 rounded-[10px] bg-white border-2 border-[#E5E7EB] disabled:bg-[#FF6A2A] disabled:border-[#FF6A2A] disabled:text-white hover:border-[#FF6A2A] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Current Floor Display */}
        {currentFloor === 'lower' ? renderLowerDeck() : renderUpperDeck()}
      </div>

      {/* Selected Seats Summary */}
      {safeSelectedSeats.length > 0 && (
        <div className="p-4 bg-[#FFF5F0] border-2 border-[#FF6A2A] rounded-[12px]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-[#111827] mb-2">
                Asientos seleccionados ({safeSelectedSeats.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {safeSelectedSeats.map((seat) => (
                  <span
                    key={seat}
                    className="px-3 py-1.5 bg-white text-[#FF6A2A] text-sm font-bold rounded-[8px] border-2 border-[#FF6A2A]"
                  >
                    {seat}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => safeSelectedSeats.forEach(onSeatSelect)}
              className="text-sm text-[#FF6A2A] hover:text-[#C2410C] font-semibold underline ml-4"
            >
              Limpiar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};